/* eslint-disable react/destructuring-assignment */

import * as vp from 'vopay';
import { FunctionComponent, JSX, useContext } from 'react';

import AccountCreationContext from './AccountCreationContext';

import type { NewAccountData } from '../types';
import type { FieldData, SubSection } from '../../../hooks/useFields/types';

const { formatAsCurrency, Box, Icon, Table, SubTitle } = vp;

type Row = {
  [key: string]: string | JSX.Element;
};

const MultiStepReview: FunctionComponent<Record<never, never>> = () => {
  const { useFields, values } = useContext(AccountCreationContext);
  const { getSection, getSectionIds } = useFields();
  const ctx = useContext(AccountCreationContext);

  const reviewData = getSectionIds().map((sectionId) => {
    const section = getSection(sectionId);
    return section.map((subsection) => {
      const tableProps: vp.TableProps = {
        name: `${sectionId}-${subsection.label}`,
        variant: 'pair',
        columns: {
          key: { header: 'Key' },
          input: { header: 'Input' },
        },
        rows: buildRows(values, subsection),
      };

      return (
        hasRows(tableProps) && (
          <Box key={`${sectionId}-${subsection.label}`} mb="lg" flx="col">
            <Box pt="lg" flx="col">
              <SubTitle subTitle={subsection.label} variant="small" />
              <Table {...tableProps} />
            </Box>
          </Box>
        )
      );
    });
  });

  return <Box flx="col">{reviewData}</Box>;

  function buildRows(newAccountData: NewAccountData, subsection: SubSection<NewAccountData>): Row[] {
    return Object.values(subsection.fields)
      .filter((feildData) => feildData.showField(ctx))
      .map((fieldData) => buildRow(fieldData, (newAccountData[fieldData.fieldName] as string | boolean) || ''))
      .filter(({ input }) => input);
  }

  function buildRow(fieldData: FieldData<NewAccountData>, input: string | boolean): Row {
    return {
      key: fieldData.label,
      input: getDataTypeHandler(fieldData)(input),
    };
  }

  function getDataTypeHandler(
    fieldData: FieldData<NewAccountData>,
  ): (userInput: string | boolean) => string | JSX.Element {
    const handlers = {
      number(userInput: string | boolean) {
        return hasStringInput(userInput) ? userInput : '';
      },

      boolean(userInput: string | boolean) {
        return hasBooleanInput(userInput) ? <Icon icon="check" size="sm" /> : '';
      },

      text(userInput: string | boolean) {
        return hasStringInput(userInput) ? userInput : '';
      },

      currency(userInput: string | boolean) {
        return hasStringInput(userInput) ? (
          <Box ai="center">
            <Icon icon="dollar" size="sm" />
            {formatAsCurrency((Math.round(parseInt(userInput.replace(/,/g, ''), 10) * 100) / 100).toFixed(2), '')}
          </Box>
        ) : (
          ''
        );
      },

      percentage(userInput: string | boolean) {
        return hasStringInput(userInput) ? (
          <Box ai="center">
            <Icon icon="percentage" size="sm" />
            {userInput}
          </Box>
        ) : (
          ''
        );
      },

      email(userInput: string | boolean) {
        return hasStringInput(userInput) ? userInput : '';
      },

      mapped(userInput: string | boolean) {
        const map = (fieldData.getSelectOptions && fieldData.getSelectOptions(ctx)) || {};
        return hasStringInput(userInput) ? map[userInput] : '';
      },

      error() {
        return '';
      },
    };

    return handlers[fieldData.dataType];
  }

  function hasStringInput(_userInput: string | boolean): _userInput is string {
    return typeof _userInput === 'string' && _userInput !== '';
  }

  function hasBooleanInput(_userInput: string | boolean): _userInput is boolean {
    return typeof _userInput === 'boolean' && _userInput === true;
  }

  function hasRows({ rows }: vp.TableProps) {
    return rows.length > 0;
  }
};

export default MultiStepReview;
